<template>
  <v-card class="eie-bank-accounts">
    <v-card-title class="toolbar primary white--text">
      <h3 class="mr-3">
        Bank Accounts ({{ bankAccounts && bankAccounts.length || 0 }})
      </h3>
      <v-spacer />
      <div class="ml-5">
        <v-select
          v-model="searchColumn"
          class="ml-5 mr-3"
          dark
          :items="searchColumnOptions"
          label="Search by"
          item-text="title"
          item-value="value"
          hide-details
          @change="onChangePagination"
        />
      </div>

      <v-text-field
        v-model="selectedValue"
        primary
        dark
        solo-inverted
        flat
        append-icon="search"
        :label="`Search by ${searchColumn}`"
        single-line
        hide-details
      />
      <v-tooltip
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            dark
            flat
            icon
            @click="clearEIEBankAccountsData().then(onChangePagination)"
            v-on="on"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh bank accounts</span>
      </v-tooltip>
    </v-card-title>
    <!-- data table results filters -->
    <v-card-title class="filters grey lighten-5">
      <div class="filters__header">
        <v-tooltip
          top
          dark
        >
          <template v-slot:activator="{ on }">
            <v-btn
              dark
              flat
              icon
              class="ma-0"
              :color="!hasFilters ? 'secondary' : 'primary'"
              v-on="on"
              @click="() => hasFilters ? resetFilters() : null"
            >
              <v-icon>
                {{ hasFilters ? 'restore' : 'filter_list' }}
              </v-icon>
            </v-btn>
          </template>
          <span>{{ hasFilters ? 'Reset ' : '' }}Filters</span>
        </v-tooltip>
      </div>
      <v-select
        v-model="filters.status"
        class="ma-1"
        :items="statusOptions"
        label="Status"
        item-text="title"
        item-value="status"
        item-key="status"
        multiple
        hide-details
      />
    </v-card-title>

    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="filteredBankAccounts"
      :pagination.sync="pagination"
      :total-items="totalElements"
      :rows-per-page-items="rowsPerPageItems"
      :loading="loading"
      select-all
      item-key="id"
      class="elevation-1"
    >
      <template
        slot="headers"
        slot-scope="props"
      >
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="[
              'column sortable',
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : ''
            ]"
            @click="changeSort(header.value)"
          >
            <v-icon small>
              arrow_upward
            </v-icon>
            {{ header.text }}
          </th>
        </tr>
      </template>
      <template
        slot="items"
        slot-scope="props"
      >
        <tr>
          <td class="text-xs-center">
            <v-btn
              style="text-transform: none;"
              outline
              round
              color="grey"
              :href="`/#/users/${props.item.userId}`"
            >
              <v-icon>
                assignment_ind
              </v-icon>
              {{ props.item.userId }}
            </v-btn>
          </td>
          <td class="text-xs-center">
            {{ props.item.bic }}
          </td>
          <td class="text-xs-center">
            {{ props.item.iban }}
          </td>
          <td class="text-xs-center">
            {{ props.item.id }}
          </td>
          <td class="text-xs-center">
            <v-chip
              :color="getStatus(props.item.status).color"
              outline
              small
            >
              {{ getStatus(props.item.status).title || 'Unknown' }}
              <v-icon
                v-if="!getStatus(props.item.status).title"
                small
                color="error"
                class="ml-1"
              >
                error
              </v-icon>
            </v-chip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.createdAt) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.createdAt) }}
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-chip
                  color="grey"
                  outline
                  small
                  v-on="on"
                >
                  <span>
                    {{ formatDateSmall(props.item.updatedAt) }}
                  </span>
                </v-chip>
              </template>
              {{ formatDate(props.item.updatedAt) }}
            </v-tooltip>
          </td>
          <td class="text-xs-center">
            <v-tooltip
              top
              dark
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  v-if="(props.item.status !== 'hidden' || props.items.status !== 'removing')"
                  flat
                  icon
                  :color="props ? 'blue darken-1' : ''"
                  @click="showHideDialog(props.item)"
                  v-on="on"
                >
                  <v-icon small>
                    {{ 'no_accounts' }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ 'Hide / Deactivate bank account' }}</span>
            </v-tooltip>
          </td>
        </tr>
        <tr v-if="props.item.error">
          <td
            colspan="9"
            class="text-xs-left errorTd"
          >
            <div class="errorRow">
              <v-icon color="red">
                info
              </v-icon> {{ props.item.error }}
            </div>
          </td>
        </tr>
      </template>
    </v-data-table>
    <HideBankAccountDialog
      :visible="hideDialog"
      :user-id="hideDialogUserId"
      :iban="hideDialogIban"
      @close="hideHideDialog()"
      @submit="hideBankAccount()"
    />
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import { setQuery } from '@/helpers';
import EieBankAccountStatus from '@/enum/eie/EieBankAccountStatus';
import HideBankAccountDialog from '@/components/eie/bankAccounts/dialogs/HideBankAccountDialog.vue';

export default {
  name: 'EieBankAccountsTable',
  components: { HideBankAccountDialog },
  props: {
    bankAccounts: {
      type: Array,
      default: () => [],
    },
    totalElements: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      hideDialog: false,
      hideDialogItem: null,
      hideDialogUserId: '',
      hideDialogIban: '',
      selected: [],
      selectedBankAccount: null,
      selectedValue: '',
      filteredBankAccounts: [],
      fetchTimeout: null,
      itemUpdating: null,
      confirmDialogOpen: false,
      searchColumn: '',
      searchColumnOptions: [
        { title: 'User Id', value: 'userId' },
      ],
      statusOptions: Object.keys(EieBankAccountStatus).map(key => EieBankAccountStatus[key]),
      pagination: {
        page: 1,
        rowsPerPage: 25,
        sortBy: 'createdAt',
        descending: true,
      },
      rowsPerPageItems: [
        {
          text: '5',
          value: 5,
        },
        {
          text: '10',
          value: 10,
        },
        {
          text: '25',
          value: 25,
        },
        {
          text: '50',
          value: 50,
        },
        {
          text: '100',
          value: 100,
        },
      ],
      filters: {
        status: [],
      },
      headers: [
        {
          text: 'User Id',
          align: 'left',
          sortable: true,
          value: 'userId',
        },
        {
          text: 'BIC',
          align: 'center',
          sortable: true,
          value: 'bic',
        },
        {
          text: 'Iban',
          align: 'center',
          sortable: true,
          value: 'iban',
        },
        {
          text: 'Bank Account Id',
          align: 'center',
          sortable: true,
          value: 'id',
        },
        {
          text: 'Status',
          align: 'center',
          sortable: true,
          value: 'status',
        },
        {
          text: 'Created',
          align: 'center',
          sortable: true,
          value: 'createdAt',
        },
        {
          text: 'Updated',
          align: 'center',
          sortable: true,
          value: 'updatedAt',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
        },
      ],
    };
  },
  computed: {
    hasFilters() {
      return (
        this.filters.status.length > 0
      );
    },
  },
  watch: {
    bankAccounts() {
      this.filterBankAccounts();
      this.$forceUpdate();
    },
    searchColumn(newVal, oldVal) {
      clearTimeout(this.fetchTimeout);

      if (!!oldVal && !!newVal) {
        this.selectedValue = null;
      }
      if (newVal === 'userId') {
        this.selectedValue = null;
      }
      this.resetFilters();
    },
    filters: {
      handler(val) {
        setQuery(
          {
            search: val.search,
          },
          this.$route,
          this.$router,
        );

        this.filterBankAccounts();
      },
      deep: true,
    },
    selectedValue(newVal) {
      clearTimeout(this.fetchTimeout);

      if (newVal) {
        this.fetchTimeout = setTimeout(async () => {
          this.clearEIEBankAccountsData().then(this.onChangePagination());
        }, 500);
      }
    },
    pagination() {
      this.onChangePagination();
    },
  },
  beforeMount() {
    this.pagination = {
      page: Number(this.$route.query.page) || 1,
      rowsPerPage: Number(this.$route.query.rowsPerPage) || 25,
      sortBy: this.$route.query.sortBy || 'createdAt',
      descending: Boolean(this.$route.query.descending) || true,
    };
    this.searchColumn = this.$route.query.searchColumn || 'userId';
    this.selectedValue = this.$route.query.selectedValue || 'userId';
  },
  mounted() {
    this.filterBankAccounts();
  },
  methods: {
    ...mapActions(['fetchEIEBankAccounts', 'clearEIEBankAccountsData', 'hideEIEBankAccount']),
    filterBankAccounts() {
      this.filteredBankAccounts = !this.hasFilters
        ? [...this.bankAccounts]
        : this.bankAccounts
          .filter((item) => {
            let hasStatus = false;

            // status
            if (!!this.filters.status.length > 0) {
              this.filters.status.forEach((option) => {
                if (item.status === option) {
                  hasStatus = true;
                }
              });
            } else {
              hasStatus = true;
            }

            return hasStatus;
          });
    },
    formatDate(date) {
      return this.$moment(date).format('MMMM Do YYYY, h:mm:ss a');
    },
    formatDateSmall(date) {
      return this.$moment(date).format('DD/MM/YYYY');
    },
    getStatus(status) {
      return this.statusOptions.find(item => item.status === status) || {};
    },
    onChangePagination() {
      setQuery(
        {
          searchColumn: this.searchColumn,
          selectedValue: this.selectedValue,
          // search: this.filters.search,
          ...this.pagination,
        },
        this.$route,
        this.$router,
      );

      const params = {
      };

      if (this.searchColumn === 'userId' && this.selectedValue) {
        params.userId = this.selectedValue;
      }

      this.fetchTimeout = setTimeout(async () => {
        await this.fetchEIEBankAccounts(params);
        this.filterBankAccounts();
      }, 1000);
    },
    showHideDialog(item) {
      this.hideDialogItem = item;
      this.hideDialogUserId = item.userId;
      this.hideDialogIban = item.iban;
      this.hideDialog = true;
      this.$forceUpdate();
    },
    hideHideDialog() {
      this.hideDialog = false;
      this.$forceUpdate();
    },
    async hideBankAccount() {
      this.itemUpdating = this.hideDialogItem;

      await this.hideEIEBankAccount({
        userId: this.hideDialogItem.userId,
        iban: this.hideDialogItem.iban,
      });
      this.filterBankAccounts();

      await this.$nextTick();
      this.itemUpdating = null;
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending;
      } else {
        this.pagination.sortBy = column;
        this.pagination.descending = false;
      }
      this.onChangePagination();
    },
    resetFilters() {
      this.filters.status = [];
    },
    cleanFields(data) {
      return Object.keys(data)
        .filter(field => !!data[field] && typeof data[field] !== 'object');
    },

    formatValue(value, key) {
      if (key === 'total' || key === 'unit_price') {
        return this.centsToEuros(value);
      }
      if (key.includes('_at')) {
        return this.formatDate(value);
      }

      return value;
    },
  },
};
</script>

<style lang="scss" scoped>
.eie-bank-accounts {
  .toolbar {
    position: relative;
    padding: 24px 24px 16px 24px;
  }
  .errorRow {
    width: 98%;
    position: absolute;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    float: left;
  }

  .errorTd {
    padding-bottom: 32px !important;
    .v-icon {
      vertical-align: middle;
    }
  }

  a {
    text-decoration: none;
    color: black;
  }

  .toolbar {
    background-color: #1a4c7d;
    color: white;
  }
  label.v-label.v-label--active.theme--dark.primary--text {
      color: white !important;
  }
  .infoLabel {
    margin-left: 16px;
  }

  .countLabel {
    color: #fff;
  }
  table.v-table tbody td,
  .v-datatable thead th {
      padding: 0 12px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/eie/bankAccounts/EieBankAccountsTable.vue